import React, { useEffect, useCallback } from 'react';
import { Button, Spin } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import 'react-quill/dist/quill.snow.css';
import { useHistory } from 'react-router-dom';
import ReactHtmlParser from 'react-html-parser';
import '../Configure.css';
import { useSelector, useDispatch } from 'react-redux';
import ConfigHeading from '../components/ConfigHeading';
import routeLocations from '../../../../utility/route-locations';
import { requestUserGroupDetails } from '../../../../state/ui/configuration/company/companyUiActions';
import { selectUserGroup } from '../../../../state/domain/company/companyReducer';
import { userIsAdmin, substituteEmailParams, displayAddressHTML } from './user-util';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import './ContactDetails.css';
import { requestSpecificEmailContent } from '../../../../state/ui/configuration/users/emailUiActions';
import { selectEmailContentMap } from '../../../../state/domain/emails/emailReducer';
import { LogoLocations } from '../../../../components/logo/ViaLogo';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';


const emailType = 'INSPECTION_REPORT'; // Only inspection report for now.


/**
 * A preview of what will be sent in an email.
 */
export default function EmailContent() {
  const history = useHistory();
  const intl = useIntl();

  // Redux Hooks
  const emailContentMap = useSelector(selectEmailContentMap);
  const sessionUser = useSelector(selectSessionUser);
  const userGroup = useSelector(selectUserGroup);
  const dispatch = useDispatch();
  const loadPageData = useCallback(() => dispatch(requestSpecificEmailContent(emailType)), [dispatch]);
  const loadUserGroup = useCallback(() => dispatch(requestUserGroupDetails()), [dispatch]);

  // Load page data on entry.
  useEffect(() => {
    loadPageData(emailType);
    if (!userGroup) loadUserGroup();
  }, [loadPageData, loadUserGroup, userGroup]);

  // Set up the html
  let html = userGroup && emailContentMap[emailType] ? emailContentMap[emailType].template : null;
  if (html) {
    const logo = `<img width="180" src="{logo-data}" alt="${userGroup.description}" style="display:block; margin: 10px;"/>`;
    const logoUrl = `data:${userGroup.logoType};base64,${userGroup.logo}`;
    html = html.replace('{header}', emailContentMap[emailType].header);
    html = html.replace('{footer}', emailContentMap[emailType].footer);
    html = html.replace('{inspection-report-email-content}', emailContentMap[emailType].emailContent);
    html = substituteEmailParams(
      html,
      {
        title: intl.formatMessage({ id: 'inspectionHeaderRegNo' }, { regNo: '{Reg Number}' }),
        companyName: userGroup.description,
        companyAddress: displayAddressHTML(userGroup.address),
        customerName: '{Customer Name}',
        locationEmail: '{Location Email}',
        logo,
        logoUrl,
        poweredByLogo: LogoLocations.POWERED_NORMAL_MOBLYX_BLUE_CIRCLE_WHITE_M,
        poweredByAlt: intl.formatMessage({ id: 'poweredBy' }),
        contentNotMoblyx: intl.formatMessage({ id: 'disclaimerStatement' }),
        emailUnwanted: intl.formatMessage({ id: 'emailUnwanted' }),
      },
    );
  }

  return (
    <ConfigHeading
      titleId="emailContent"
      descriptionId="emailContentDesc"
      actions={userIsAdmin(sessionUser) && (
        <Button type="primary" icon="edit" loading={!userGroup || !emailContentMap[emailType.toUpperCase()]} onClick={() => history.push(`${routeLocations.configure_edit_email_content}/${emailType}`)}>
          <FormattedMessage id="edit" />
        </Button>
      )}
    >
      <ViaValidationAlert validationError={null} />
      {
        emailContentMap[emailType.toUpperCase()]
          ? <div className="quill-style">{ReactHtmlParser(html)}</div>
          : <div className="loading-wrapper"><Spin size="large" /></div>
      }
    </ConfigHeading>
  );
}
